import { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { FormattedMessage } from "react-intl";
import { showAllActivities as showAllActivitiesAction } from "app/pages/Booking/Quotation/quotationActionCreators";
import { updateBookingActivity as updateBookingActivityAction } from "app/pages/Booking/bookingActionCreators";
import QuotationActivities from "app/pages/Booking/Quotation/Activity/QuotationActivities";
import {
	getActivities,
	shouldNotShowAllActivities,
} from "app/pages/Booking/Quotation/quotationSelectors";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { Button } from "app/components/ui/Button/Button";

const QuotationActivitiesContainer = ({ error, activityRef = {} }) => {
	const { shouldNotShowAllActivities: shouldNotShowAllActivitiesValue = false, activities = [] } =
		useSelector(mapStateToProps, shallowEqual);

	const dispatch = useDispatch();
	const showAllActivities = useCallback(
		(...args) => dispatch(showAllActivitiesAction(...args)),
		[dispatch]
	);
	const updateBookingActivity = useCallback(
		(...args) => dispatch(updateBookingActivityAction(...args)),
		[dispatch]
	);

	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	useEffect(() => {
		if (isElementOnScreen && activities.length > 0) {
			track("quote_view_activity", {
				activities: activities.map(activity => activity.name),
			});
		}
	}, [isElementOnScreen, activities]);

	const handleShowAllActivities = useCallback(() => {
		showAllActivities();
		track("activity_show_more");
	}, []);

	const onActivitiesChange = useCallback(activity => {
		updateBookingActivity(activity);
	}, []);

	return (
		activities?.length > 0 && (
			<div className="quotation__item" ref={activityRef}>
				<FormattedMessage id="quotation.activity.title">
					{message => (
						<div className="quotation__headline">
							<Typography
								variant={TYPOGRAPHY_VARIANTS.XL3}
								variantMD={TYPOGRAPHY_VARIANTS.XL4}
								isBold
								className="quotation__title"
								component="h2"
							>
								{message}
							</Typography>
						</div>
					)}
				</FormattedMessage>
				<div className="quotation__content" ref={ref}>
					<QuotationActivities
						activities={activities}
						onChange={onActivitiesChange}
						error={error}
					/>
					{shouldNotShowAllActivitiesValue && (
						<div className="quotation__show-more">
							<Button
								variant="secondary"
								onClick={handleShowAllActivities}
								data-testid="show-more-activities-button"
							>
								<FormattedMessage id="quotation.activity.view.more" />
							</Button>
						</div>
					)}
				</div>
			</div>
		)
	);
};

QuotationActivitiesContainer.propTypes = {
	error: PropTypes.object,
	activityRef: PropTypes.any,
};

const mapStateToProps = state => ({
	booking: state.booking,
	brand: state.brand,
	activities: getActivities(state),
	shouldNotShowAllActivities: shouldNotShowAllActivities(state),
});

export default QuotationActivitiesContainer;
